/**
 * MemberSidebar — the member portal's left navigation rail. Mirrors the staff
 * web app's V2 sidebar design (same v2 tokens, collapse behaviour, section
 * headers and item styling) so members get the same look & navigation model as
 * the web application, but is self-contained: member auth, a static member nav
 * gated by the member's active-org features, and the org switcher — no
 * staff-only providers (branding / nav-config / capabilities), which the member
 * portal doesn't mount.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Home,
  GraduationCap,
  BookOpen,
  Inbox,
  FileText,
  ArrowLeftRight,
  ClipboardList,
  Award,
  Briefcase,
  StickyNote,
  Bell,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Heart,
  Users,
  Wallet,
  ShieldCheck,
} from 'lucide-react';
import { getMemberToken, getMemberData, logoutMember } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { MemberOrgSwitcher } from '@/components/member-portal/MemberOrgSwitcher';
import { MemberThemeToggle } from '@/components/member-portal/MemberThemeToggle';
import { MemberNotificationBell } from '@/components/member-portal/MemberNotificationBell';

interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  route: string;
  /** Only show when this active-org feature is available (from /features). */
  feature?: 'training' | 'benefits' | 'incentives' | 'doula' | 'verify';
}
interface NavSection {
  key: string;
  label?: string;
  /** Shown only to an account that is a doula on a panel. */
  doulaOnly?: boolean;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    key: 'main',
    items: [{ id: 'dashboard', label: 'Home', icon: Home, route: '/member/dashboard' }],
  },
  {
    key: 'learning',
    label: 'Learning',
    items: [
      { id: 'training', label: 'My Training', icon: GraduationCap, route: '/member/training', feature: 'training' },
      { id: 'courses', label: 'Courses', icon: BookOpen, route: '/member/courses', feature: 'training' },
    ],
  },
  {
    key: 'my_info',
    label: 'My Info',
    items: [
      { id: 'inbox', label: 'Messages', icon: Inbox, route: '/member/inbox' },
      { id: 'notifications', label: 'Notifications', icon: Bell, route: '/member/notifications' },
      { id: 'documents', label: 'Documents', icon: FileText, route: '/member/documents' },
      { id: 'referrals', label: 'Referrals', icon: ArrowLeftRight, route: '/member/referrals' },
      { id: 'find-a-doula', label: 'Find a Doula', icon: Heart, route: '/member/find-a-doula', feature: 'doula' },
      { id: 'enrollments', label: 'Programs & Benefits', icon: ClipboardList, route: '/member/enrollments' },
      { id: 'incentives', label: 'Incentives', icon: Award, route: '/member/incentives', feature: 'incentives' },
      { id: 'work-compliance', label: 'Work Requirements', icon: Briefcase, route: '/member/work-compliance', feature: 'benefits' },
      { id: 'verified-hours', label: 'My Verified Hours', icon: ShieldCheck, route: '/member/verified-hours', feature: 'verify' },
      { id: 'notes', label: 'Notes', icon: StickyNote, route: '/member/notes' },
    ],
  },
  // Only for a doula, and gated on identity rather than on a feature flag. A
  // feature flag says "this organisation does doula care", which is true for the
  // mothers too — gating on it would put "My clients" in a mother's sidebar.
  {
    key: 'doula_work',
    label: 'My Work',
    doulaOnly: true,
    items: [
      { id: 'doula-caseload', label: 'My Clients', icon: Users, route: '/doula/caseload' },
      { id: 'doula-offers', label: 'Offers', icon: Inbox, route: '/doula/offers' },
      { id: 'doula-payments', label: 'My Payments', icon: Wallet, route: '/doula/payments' },
      { id: 'doula-profile', label: 'My Profile', icon: Heart, route: '/doula/profile' },
    ],
  },
];

const COLLAPSE_KEY = 'member_sidebar_collapsed';
const SECTIONS_COLLAPSE_KEY = 'member_sidebar_collapsed_sections';

export function MemberSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppTranslation();
  const member = getMemberData();

  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  );
  const [features, setFeatures] = useState<Record<string, boolean> | null>(null);
  // Starts false and stays false unless the server says otherwise. Unlike the
  // feature flags above this FAILS CLOSED: showing a caseload link to a mother
  // is worse than a doula missing one, and she has the routes either way.
  const [isDoula, setIsDoula] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Per-section collapse. Independently expandable nav groups, keyed by
  // section key and persisted so the layout survives reloads. Empty set = all
  // expanded (the default). Only applies to labeled sections in the expanded
  // rail — the 60px rail has no section headers, so items always show.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(SECTIONS_COLLAPSE_KEY);
      return raw ? new Set<string>(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleSection = useCallback((sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      try {
        localStorage.setItem(SECTIONS_COLLAPSE_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore persistence errors (private mode, quota) */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getMemberToken();
        if (!token) return;
        const res = await fetch(getWeaveinApiUrl('/api/member-portal/features'), {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setFeatures(data.features || null);
        }
      } catch {
        /* fail open — leave features null so all items show */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getMemberToken();
        if (!token) return;
        // 403 here is the ordinary answer for an account that is not on a panel,
        // which is most accounts. It is not an error and is not reported.
        const res = await fetch(getWeaveinApiUrl('/api/doula/v1/me'), {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          },
        });
        if (res.ok && !cancelled) setIsDoula(true);
      } catch {
        /* not a doula, or we could not tell — leave it closed */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(
    () =>
      NAV_SECTIONS.filter((s) => !s.doulaOnly || isDoula).map((s) => ({
        ...s,
        // null features (loading / failed) → show everything (fail-open).
        items: s.items.filter((it) => !it.feature || features === null || features[it.feature]),
      })).filter((s) => s.items.length > 0),
    [features, isDoula],
  );

  const isActive = useCallback(
    (route: string) =>
      location.pathname === route || location.pathname.startsWith(`${route}/`),
    [location.pathname],
  );

  const handleLogout = () => {
    logoutMember();
    navigate('/member/home');
  };

  const memberName =
    [member?.first_name, member?.last_name].filter(Boolean).join(' ') ||
    member?.email ||
    'Member';
  const initials =
    (member?.first_name?.[0] || '') + (member?.last_name?.[0] || '') || 'M';

  return (
    <div
      className={`flex h-screen flex-col border-r border-v2border bg-paper ${
        collapsed ? 'w-[60px]' : 'w-[240px]'
      } transition-[width] duration-150`}
      style={{ padding: 12, gap: 8, overflowY: 'auto' }}
    >
      {/* Brand + collapse */}
      <div className="flex items-center justify-between px-1.5 pb-2 pt-1">
        {!collapsed && (
          <div className="flex min-w-0 items-center gap-2 font-display text-sm font-semibold text-ink">
            <span className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
              <Heart className="h-3.5 w-3.5" />
            </span>
            <span className="truncate">{t('dashboard.memberPortal', 'Member Portal')}</span>
          </div>
        )}
        <div className="flex items-center gap-0.5">
          {/* Quick notification tray (dropdown). Expanded only — collapsed
              members reach notifications via the "Notifications" nav item. */}
          {!collapsed && <MemberNotificationBell collapsed />}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="rounded-[var(--v2-r-sm)] p-1 text-ink-400 hover:bg-paper-2 hover:text-ink"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>
      </div>

      {/* Org switcher (dual/multi-org members) */}
      {!collapsed && (
        <div className="px-0.5">
          <MemberOrgSwitcher />
        </div>
      )}

      {/* Nav sections */}
      {sections.map((section) => {
        // Only labeled sections get a collapse toggle; label-less groups (e.g.
        // the standalone Home item) always render their items.
        const collapsible = !collapsed && !!section.label;
        const isSectionCollapsed = collapsible && collapsedSections.has(section.key);
        return (
        <div key={section.key}>
          {!collapsed && section.label && (
            <button
              onClick={() => toggleSection(section.key)}
              aria-expanded={!isSectionCollapsed}
              className="group flex w-full items-center gap-1 rounded-[var(--v2-r-sm)] px-2 pb-1 pt-3 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-400 hover:text-ink-700"
            >
              <ChevronDown
                size={11}
                className={`flex-shrink-0 text-ink-300 transition-transform duration-150 group-hover:text-ink-500 ${isSectionCollapsed ? '-rotate-90' : ''}`}
              />
              <span className="flex-1 text-left">{section.label}</span>
            </button>
          )}
          {!isSectionCollapsed && section.items.map((item) => {
            const active = isActive(item.route);
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.route)}
                className={`flex w-full items-center gap-2.5 rounded-[var(--v2-r-sm)] px-[9px] py-[7px] text-[13px] font-medium transition-colors duration-75 ${
                  active
                    ? 'bg-[#14171c] text-white dark:bg-white dark:text-[#14171c]'
                    : 'text-ink-700 hover:bg-paper-2 hover:text-ink'
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={14} className="flex-shrink-0" />
                {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
              </button>
            );
          })}
        </div>
        );
      })}

      {/* Footer: quick theme toggle + user + settings + logout */}
      <div className="mt-auto flex flex-col gap-1 border-t border-v2border pt-2">
        <MemberThemeToggle collapsed={collapsed} />
        <button
          onClick={() => navigate('/member/settings')}
          className={`flex w-full items-center gap-2.5 rounded-[var(--v2-r-sm)] px-[9px] py-[7px] text-[13px] font-medium text-ink-700 transition-colors hover:bg-paper-2 hover:text-ink ${
            collapsed ? 'justify-center' : ''
          }`}
          title={collapsed ? t('nav.settings', 'Settings') : undefined}
        >
          <Settings size={14} className="flex-shrink-0" />
          {!collapsed && <span className="flex-1 text-left">{t('nav.settings', 'Settings')}</span>}
        </button>
        <button
          onClick={handleLogout}
          className={`flex w-full items-center gap-2.5 rounded-[var(--v2-r-sm)] px-[9px] py-[7px] text-[13px] font-medium text-ink-700 transition-colors hover:bg-paper-2 hover:text-ink ${
            collapsed ? 'justify-center' : ''
          }`}
          title={collapsed ? t('nav.logout', 'Logout') : undefined}
        >
          <LogOut size={14} className="flex-shrink-0" />
          {!collapsed && <span className="flex-1 text-left">{t('nav.logout', 'Logout')}</span>}
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2 px-1.5 pt-1">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
              {initials.toUpperCase()}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12px] text-ink-600" title={memberName}>
              {memberName}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
